import type { GameContext, GameMiddleware } from "./types";

// URL must match the one in client/index.html
const JQUERY_CDN = "//ajax.googleapis.com/ajax/libs/jquery/2.1.1/jquery.min.js";
const GUI_STYLESHEET = "/gui/dialogs/templates/style.less";

/**
 * Initialises the game GUI system: loads jQuery (if absent), injects the GUI
 * stylesheet (if absent), then lazily imports gui/ui.ts so it only evaluates
 * after jQuery is on window, loads dialog templates, and sets up the debug dialog.
 *
 * Works in both contexts:
 * - Client: jQuery + stylesheet already on page → both ensure* calls are no-ops.
 * - Editor: jQuery and stylesheet injected dynamically; UI is hidden in editor
 *   mode and shown when onModeChange("preview") is called.
 */
export class GUIMiddleware implements GameMiddleware {
    private _ui: any = null;
    private _debugGui: any = null;

    async init(_ctx: GameContext): Promise<void> {
        await this._ensureJQuery();
        this._ensureStylesheet();

        // Dynamic eager import: bundled inline but not evaluated until here,
        // so gui/ui.ts runs after window.$ is defined.
        const { ui } = await import(
            /* webpackMode: "eager" */
            "../../gui/ui"
        );
        this._ui = ui;
        (window as any).ui = ui;

        await new Promise<void>((resolve) => {
            const doLoad = () => {
                ui.loadTemplates("/gui/dialogs/templates", () => {
                    this._debugGui = ui.debugDialog();
                    (window as any).debugGui = this._debugGui;
                    const DebugHelper: any = (window as any).DebugHelper;
                    DebugHelper?.init?.();
                    resolve();
                });
            };

            if (document.readyState === "loading") {
                window.addEventListener("DOMContentLoaded", doLoad, { once: true });
            } else {
                doLoad();
            }
        });
    }

    onModeChange(mode: "editor" | "preview"): void {
        if (!this._ui) return;

        if (mode === "preview") {
            this._ui._element.show();
            this._debugGui?.show();
        } else {
            this._ui._element.hide();
        }
    }

    private _ensureStylesheet(): void {
        if (!document.querySelector('link[href*="style.less"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = GUI_STYLESHEET;
            document.head.appendChild(link);
        }
    }

    private _ensureJQuery(): Promise<void> {
        if (typeof (window as any).$ !== "undefined") return Promise.resolve();

        return new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = JQUERY_CDN;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`[GUIMiddleware] Failed to load jQuery from ${JQUERY_CDN}`));
            document.head.appendChild(script);
        });
    }
}
